/* LAV TDMS — печатная форма листа замечаний (CRS).

   Лист адресован подрядчику, поэтому вид один на две стороны: заказчик
   открывает его из рассмотрения, подрядчик — из своего замечания. Держать две
   копии формы значит однажды напечатать разное по одному и тому же листу. */
import { useState, type CSSProperties } from 'react'
import { Badge, Button, CodeChip, Dialog, Spinner, toast } from '@/ui'
import type { Tone } from '@/ui'
import { Icon } from '@/ds/icons'
import { useSub } from '@/data/queries'
import { saveFile } from '@/data/api'
import type { ReviewCode } from '@/data/types'
import { t, type Lang } from '@/i18n/lang'

/** Критичность замечания. Общая для формы, рассмотрения и портала подрядчика:
    три копии одной шкалы однажды разойдутся в подписях. */
export const CRIT_LABEL: Record<string, { ru: string; en: string }> = {
  high: { ru: 'высокая', en: 'high' },
  med: { ru: 'средняя', en: 'medium' },
  low: { ru: 'низкая', en: 'low' },
}

export const CRIT_TONE: Record<string, Tone> = { high: 'danger', med: 'warning', low: 'neutral' }

/** Метка критичности. Заменила код рассмотрения: код у документа, не у замечания. */
export function CritBadge({ level, lang }: { level: string; lang: Lang }) {
  return (
    <Badge tone={CRIT_TONE[level] ?? 'neutral'} size="sm">
      {t(lang, CRIT_LABEL[level]?.ru ?? level, CRIT_LABEL[level]?.en ?? level)}
    </Badge>
  )
}

/** Ответ GET /crs-sheets/{code}: шапка листа и вошедшие в него замечания. */
export interface SheetForm {
  code: string
  doc: string
  title: string | null
  disc: string | null
  vendor: string | null
  rev: string | null
  /** Версия основного файла, по которой собран лист. NULL — лист старого формата. */
  ver: number | null
  incoming: string
  outgoing: string | null
  reviewCode: ReviewCode
  status: 'draft' | 'issued'
  preparedBy: string
  preparedOn: string | null
  /* carried — замечание прошлого круга, оставшееся без ответа: круг
     заканчивался, лист печатался с одними новыми, и оно исчезало из бумаги. */
  items: { id: string; page: number | null; clause: string | null; crit: string; text: string; author: string
           date: string | null; carried: boolean; fromSheet: string | null }[]
}

export function SheetPrint({ code, lang, onClose }: { code: string | null; lang: Lang; onClose: () => void }) {
  const sheet = useSub<SheetForm>(code ? `/crs-sheets/${encodeURIComponent(code)}` : null)
  const [blankBusy, setBlankBusy] = useState(false)
  const S = sheet.data
  return (
    <Dialog
      open={code != null}
      onClose={onClose}
      sizeKey="crs-sheet"
      width={860}
      title={
        <span>
          {t(lang, 'Лист замечаний CRS', 'CRS sheet')}{' '}
          <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-subtle)' }}>{code}</span>
        </span>
      }
    >
      {sheet.isPending ? (
        /* Спиннер к тексту: голая надпись неотличима от зависания. */
        <div style={{ padding: 24, font: 'var(--font-ui)', color: 'var(--text-subtle)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Spinner size={13} />
          {t(lang, 'Загрузка…', 'Loading…')}
        </div>
      ) : sheet.isError || !S ? (
        <div style={{ padding: 24, font: 'var(--font-ui)', color: 'var(--danger-ink)' }}>{t(lang, 'Не удалось загрузить лист', 'Failed to load the sheet')}</div>
      ) : (
        <>
          {/* Печатается только это поддерево: правило в ui.css гасит остальное. */}
          <div className="crs-print" style={{ background: 'var(--surface-card)', color: 'var(--text-body)' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, paddingBottom: 10, borderBottom: '2px solid var(--border-strong)' }}>
              <div style={{ flex: 1 }}>
                <div style={{ font: 'var(--font-view-title)', color: 'var(--text-strong)' }}>{S.code}</div>
                <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', marginTop: 2 }}>{t(lang, 'Лист замечаний · Comment Review Sheet', 'Comment Review Sheet')}</div>
              </div>
              <CodeChip code={S.reviewCode} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 18px', padding: '12px 0', font: 'var(--font-ui)', fontSize: 'var(--text-sm)' }}>
              <SheetField label={t(lang, 'Документ', 'Document')} value={S.doc} mono />
              <SheetField label={t(lang, 'Ревизия', 'Revision')} value={S.rev ?? '—'} mono />
              {/* Версия стоит в коде листа — она обязана быть и в шапке. */}
              {/* «Подача», а не «Версия»: число называет номер присылки внутри
                  ревизии, а «версией» в системе зовут ещё и версию файла —
                  одно слово на две величины путало обе стороны. */}
              <SheetField label={t(lang, 'Подача', 'Submission')} value={S.ver == null ? '—' : String(S.ver).padStart(2, '0')} mono />
              <SheetField label={t(lang, 'Наименование', 'Title')} value={S.title ?? '—'} />
              <SheetField label={t(lang, 'Дисциплина', 'Discipline')} value={S.disc ?? '—'} />
              <SheetField label={t(lang, 'Подрядчик', 'Contractor')} value={S.vendor ?? '—'} />
              <SheetField label={t(lang, 'Рассмотрение', 'Review')} value={S.incoming} mono />
              <SheetField label={t(lang, 'Исходящий', 'Outgoing')} value={S.outgoing ?? t(lang, 'не выпущен', 'not issued')} mono />
              <SheetField label={t(lang, 'Код рассмотрения', 'Review code')} value={S.reviewCode} mono />
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', font: 'var(--font-ui)', fontSize: 'var(--text-sm)' }}>
              <thead>
                <tr>
                  {['№', t(lang, 'Лист', 'Sheet'), t(lang, 'Пункт НТД', 'Clause'), t(lang, 'Крит.', 'Crit.'), t(lang, 'Замечание', 'Comment'), t(lang, 'Автор', 'Author')].map((h, i) => (
                    <th key={i} style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--border-strong)', font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {S.items.map((it, i) => (
                  <tr key={it.id}>
                    <td style={SHEET_CELL}>{i + 1}</td>
                    <td style={SHEET_CELL}>{it.page ?? '—'}</td>
                    <td style={SHEET_CELL}>{it.clause ?? '—'}</td>
                    <td style={SHEET_CELL}>{t(lang, CRIT_LABEL[it.crit]?.ru ?? it.crit, CRIT_LABEL[it.crit]?.en ?? it.crit)}</td>
                    <td style={{ ...SHEET_CELL, width: '55%' }}>
                      {it.text}
                      {it.carried ? (
                        <span style={{ marginLeft: 6, font: 'var(--font-caption)', color: 'var(--amber-700, var(--text-subtle))' }}>
                          · {t(lang, 'перенесено из', 'carried from')} {it.fromSheet}
                        </span>
                      ) : null}
                    </td>
                    <td style={SHEET_CELL}>{it.author}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid var(--border-default)', display: 'flex', gap: 24, font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
              <span>{t(lang, 'Подготовил', 'Prepared by')}: {S.preparedBy}</span>
              <span>{S.preparedOn ?? ''}</span>
              <span style={{ marginLeft: 'auto' }}>{t(lang, 'Замечаний', 'Comments')}: {S.items.length}</span>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <Button variant="secondary" onClick={onClose}>{t(lang, 'Закрыть', 'Close')}</Button>
            {/* Бланк заказчика собирает сервер: у него лежит сам бланк, и он же
                знает, что печатать в половине подрядчика. Здешний вид отвечает
                на другой вопрос — «что в листе», а не «как это выглядит у
                заказчика на бумаге». */}
            <Button variant="secondary" iconLeft={<Icon name="file-text" size={14} />}
                    disabled={blankBusy || code == null}
                    title={t(lang, 'Бланк CRS заказчика, заполненный данными листа', "The customer's CRS blank filled with the sheet data")}
                    onClick={() => {
                      if (!code) return
                      setBlankBusy(true)
                      saveFile(`/crs-sheets/${encodeURIComponent(code)}/form`)
                        .then((name) => toast({ tone: 'success', title: t(lang, 'Бланк собран', 'Blank ready'), text: name }))
                        .catch((e: unknown) => toast({ tone: 'danger', title: t(lang, 'Бланк не собран', 'Blank failed'),
                          text: e instanceof Error ? e.message : String(e) }))
                        .finally(() => setBlankBusy(false))
                    }}>{blankBusy ? t(lang, 'Собираем…', 'Building…') : t(lang, 'Бланк заказчика', "Customer's blank")}</Button>
            <Button variant="primary" iconLeft={<Icon name="printer" size={14} />} onClick={() => window.print()}>{t(lang, 'Печать', 'Print')}</Button>
          </div>
        </>
      )}
    </Dialog>
  )
}

const SHEET_CELL: CSSProperties = {
  padding: '6px 8px',
  borderBottom: '1px solid var(--border-subtle)',
  verticalAlign: 'top',
  color: 'var(--text-body)',
}

function SheetField({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', flex: '0 0 auto', minWidth: 110 }}>{label}</span>
      <span style={{ fontFamily: mono ? 'var(--font-mono)' : undefined, color: 'var(--text-strong)' }}>{value}</span>
    </div>
  )
}
